import { bbox, bboxPolygon, simplify, truncate } from '@turf/turf'

import { MAPBOX_ACCESS_TOKEN, MAPBOX_API } from '@/config/mapbox'

// Project boundaries have no stored thumbnail, so previews are Mapbox Static
// Images URLs built from the boundary geometry itself.

const STYLE = 'light-v11'
const DEFAULT_COLOR = '#0059ff'
const MAX_URL_LENGTH = 8000
const SIMPLIFY_STEPS = [0.001, 0.01, 0.05]

export interface StaticMapOptions {
  width?: number
  height?: number
}

const urlCache = new Map<string, string | null>()

const coordsAreAllZero = (coords: any): boolean => {
  if (typeof coords[0] === 'number') return coords[0] === 0 && coords[1] === 0
  return coords.every((c: any) => coordsAreAllZero(c))
}

// The API substitutes a degenerate [[[0,0],[0,0],[0,0],[0,0]]] polygon for a
// NULL geometry, so a boundary can exist (place details only) with nothing to draw.
export const hasRealGeometry = (feature: any): boolean => {
  const coords = feature?.geometry?.coordinates
  if (!coords?.length) return false
  return !coordsAreAllZero(coords)
}

const overlayFeature = (geometry: any, color: string) => ({
  type: 'Feature',
  properties: {
    stroke: color,
    'stroke-width': 2,
    'stroke-opacity': 1,
    fill: color,
    'fill-opacity': 0.3
  },
  geometry
})

const buildUrl = (geometry: any, color: string, width: number, height: number): string => {
  const overlay = encodeURIComponent(JSON.stringify(overlayFeature(geometry, color)))
  return `${MAPBOX_API.STATIC}/${STYLE}/static/geojson(${overlay})/auto/${width}x${height}@2x` +
    `?padding=24&access_token=${MAPBOX_ACCESS_TOKEN}`
}

export const buildStaticMapUrl = (feature: any, options: StaticMapOptions = {}): string | null => {
  if (!hasRealGeometry(feature)) return null

  const width = options.width ?? 320
  const height = options.height ?? 180
  const cacheKey = `${feature.properties?.project_id}:${width}x${height}`
  if (urlCache.has(cacheKey)) return urlCache.get(cacheKey)!

  const color = feature.properties?.color || DEFAULT_COLOR
  let url: string | null = null

  try {
    const trimmed = truncate(feature, { precision: 4, mutate: false })
    const candidates = [
      () => trimmed.geometry,
      ...SIMPLIFY_STEPS.map((tolerance) => () =>
        simplify(trimmed, { tolerance, highQuality: false, mutate: false }).geometry),
      // Last resort: the bounding rectangle always fits in a URL.
      () => bboxPolygon(bbox(trimmed) as any).geometry
    ]

    for (const build of candidates) {
      let candidate: string
      try {
        candidate = buildUrl(build(), color, width, height)
      } catch {
        // simplify() rejects some geometry types (points) — try the next fallback.
        continue
      }
      if (candidate.length <= MAX_URL_LENGTH) {
        url = candidate
        break
      }
    }
  } catch (e) {
    console.error('Failed to build static map url', e)
  }

  urlCache.set(cacheKey, url)
  return url
}
